import S, { DataSignal } from 's-js';
import * as React from 'react';

export default function SuperCritical<P>(render : (props : P) => JSX.Element | null) {
    return class extends SuperCriticalComponent<P> {
        srender(props : P) {
            return render(props);
        }
    }
};

interface ComponentState {
    view : React.ReactElement<any> | null;
}

export abstract class SuperCriticalComponent<P> extends React.Component<P, ComponentState> {
    private readonly _sprops : DataSignal<P>;
    /** Read as "S props." The component's current props as an S-js signal. */
    readonly sprops : () => P;

    private readonly _smounted : DataSignal<boolean>;
    /** Read as "S mounted." Whether or not the component is currently mounted, as an S-js signal. */
    readonly smounted : () => boolean;

    state: ComponentState = { view: null };

    constructor(props : P) {
        super(props);
        this._sprops = S.data(props);
        this.sprops = () => this._sprops();

        let isMounted = false;
        const mountedEv = S.data(null);
        // Avoiding a normal S.value to work-around a subtle timing bug: It can be the case that a component
        // receives a componentWillUnmount event immediately after a componentWillReceiveProps event but
        // BEFORE S has re-run our srender computation below in response to the props change. If that happens,
        // we wanna let readers, including our srender computation below, know that the component is being
        // unmounted as soon as we do, even in situations where a normal S.value would hold of on notifying
        // users of the new value until the next tick of S's clock. Otherwise, calling certain methods like
        // setState on an already-unmounting component produces noisy React warnings in the dev console.
        this._smounted = (val? : boolean) => val === undefined ?
            (mountedEv(), isMounted) :
            (isMounted = val, mountedEv(null), isMounted);
        this.smounted = () => this._smounted();
        S.root(() => S(() => this.smounted() && this.setState({ view: this.srender(this.sprops()) })));
    }

    abstract srender(props : P) : JSX.Element | null;

    componentWillMount() {
        this._smounted(true);
    }

    componentWillReceiveProps(props : P) {
        if (!propsEq(this.props, props)) this._sprops(props);
    }

    componentWillUnmount() {
        this._smounted(false);
    }

    render() {
        return this.state.view;
    }
}

export const SuperCriticalReference = <P, E>(
    cmp : SuperCriticalComponent<P>, 
    init : (props : P, el : E) => void,
    destroy? : (el : E) => void
) => {
    let el : E | null = null, 
        event = S.data(null),
        ref = (_el : E | null) => { el = _el; event(null); };

    S.root(() => S<E | null>(prevEl => { 
        event();
        if (el) init(cmp.sprops(), el);
        else if (prevEl && destroy) destroy(prevEl);
        return el;
    }, null));

    return ref;
}

function propsEq<P>(a : P, b : P) {
    for (var key in b) {
        if ((b as any).hasOwnProperty(key) && (a as any)[key] !== (b as any)[key]) return false;
    }
    return true;
}



// WEBPACK FOOTER //
// ./lib/supercritical.ts